import React, { useEffect, useState, useMemo } from "react";
import {
  getUsuarios,
  postUsuario,
  putUsuario,
  addRelClienteUsuario,
  deleteRelClienteUsuario,
  exportarUsuariosExcel,
} from "../../componentes/http/actions/UsuarioAction";
import { getClienteHabilitado } from "../../componentes/http/actions/ClientesAction";
import EditNoteIcon from "@mui/icons-material/EditNote";
import {
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Grid,
  Typography,
  Box,
  Snackbar,
  Alert,
  TextField,
  TablePagination,
  InputAdornment,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  MenuItem as SelectMenuItem,
  Collapse,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import PersonRemoveAlt1Icon from "@mui/icons-material/PersonRemoveAlt1";
import DomainIcon from "@mui/icons-material/Domain";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import NuevoUsuarioModal from "../../componentes/modal/Usuarios/NuevoUsuarioModal";
import EditarUsuarioModal from "../../componentes/modal/Usuarios/EditarUsuarioModal";
import AgregarClienteUsuarioModal from "../../componentes/modal/Usuarios/AgregarClienteUsuarioModal";
import { Link } from "react-router-dom";

const ESTADO_ACCESO_OPCIONES = [
  { value: "todos", label: "Todos" },
  { value: "accedieron", label: "Han accedido" },
  { value: "nunca", label: "Nunca accedieron" },
];

const GestorUsuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [openAddClienteModal, setOpenAddClienteModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUsuario, setSelectedUsuario] = useState(null);

  // Filtros
  const [clientes, setClientes] = useState([]);
  const [filtroCliente, setFiltroCliente] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [filtroRol, setFiltroRol] = useState("");
  const [filtroTipoAuth, setFiltroTipoAuth] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("");
  const [filtroAcceso, setFiltroAcceso] = useState("todos");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    fetchUsuarios();
    fetchClientes();
  }, []);

  const fetchClientes = () => {
    getClienteHabilitado(localStorage.getItem("token"))
      .then((response) => setClientes(response))
      .catch((error) => console.log("Error al cargar clientes:", error));
  };

  const fetchUsuarios = () => {
    setIsLoading(true);
    getUsuarios(localStorage.getItem("token"))
      .then((response) => {
        setUsuarios(response);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setIsLoading(false);
        setInitialDataLoaded(true);
      });
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  // Opciones dinámicas de rol a partir de los datos
  const rolesDisponibles = useMemo(() => {
    const set = new Set(usuarios.map((u) => u.rol).filter(Boolean));
    return Array.from(set);
  }, [usuarios]);

  // Filtrado combinado
  const filteredUsuarios = useMemo(() => {
    return usuarios.filter((usuario) => {
      const fullName = `${usuario.nombre} ${usuario.apellido}`.toLowerCase();
      const coincideBusqueda = fullName.includes(searchTerm.toLowerCase());

      const coincideRol = !filtroRol || usuario.rol === filtroRol;

      const esExterno = !!usuario.proveedorExterno;
      const coincideAuth =
        !filtroTipoAuth ||
        (filtroTipoAuth === "Externo" && esExterno) ||
        (filtroTipoAuth === "Local" && !esExterno);

      const coincideEstado =
        !filtroEstado ||
        (filtroEstado === "habilitado" && usuario.habilitado) ||
        (filtroEstado === "deshabilitado" && !usuario.habilitado);

      const coincideCliente =
        !filtroCliente ||
        (usuario.clientes ?? []).some((c) => c.id === parseInt(filtroCliente));

      const accedio = (usuario.cantidadIniciosSesion ?? 0) > 0;
      const coincideAcceso =
        filtroAcceso === "todos" ||
        (filtroAcceso === "accedieron" && accedio) ||
        (filtroAcceso === "nunca" && !accedio);

      return (
        coincideBusqueda &&
        coincideRol &&
        coincideAuth &&
        coincideEstado &&
        coincideAcceso &&
        coincideCliente
      );
    });
  }, [usuarios, searchTerm, filtroRol, filtroTipoAuth, filtroEstado, filtroAcceso, filtroCliente]);

  const displayedUsuarios = filteredUsuarios.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Estadísticas para el resumen
  const stats = useMemo(() => {
    const total = usuarios.length;
    const accedieron = usuarios.filter(
      (u) => (u.cantidadIniciosSesion ?? 0) > 0
    ).length;
    return { total, accedieron, nunca: total - accedieron };
  }, [usuarios]);

  const hayFiltrosActivos =
    filtroRol || filtroTipoAuth || filtroEstado || filtroAcceso !== "todos" || filtroCliente;

  const limpiarFiltros = () => {
    setFiltroRol("");
    setFiltroTipoAuth("");
    setFiltroEstado("");
    setFiltroAcceso("todos");
    setFiltroCliente("");
    setPage(0);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleOpenEditModal = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setOpenEditModal(true);
  };

  const handleCloseEditModal = () => {
    setOpenEditModal(false);
    setUsuarioSeleccionado(null);
  };

  const handleOpenAddClienteModal = (usuario) => {
    setUsuarioSeleccionado(usuario);
    setOpenAddClienteModal(true);
  };

  const handleCloseAddClienteModal = () => {
    setOpenAddClienteModal(false);
    setUsuarioSeleccionado(null);
  };

  const handleClick = (event, usuario) => {
    setAnchorEl(event.currentTarget);
    setSelectedUsuario(usuario);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedUsuario(null);
  };

  const handleRemoveCliente = (clienteId) => {
    handleRemoveClienteUsuario({
      idUsuario: selectedUsuario.id,
      idCliente: clienteId,
    });
    handleClose();
  };

  const handleNuevoUsuario = async (usuario) => {
    try {
      setIsLoading(true);
      await postUsuario(localStorage.getItem("token"), usuario);
      await fetchUsuarios();
      handleCloseModal();
      setSnackbar({
        open: true,
        message: "Usuario agregado con éxito",
        severity: "success",
      });
    } catch (error) {
      console.error("Error al agregar nuevo usuario:", error);
      setSnackbar({
        open: true,
        message: "Error al agregar usuario",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditarusuario = async (usuarioEditado) => {
    try {
      setIsLoading(true);
      await putUsuario(localStorage.getItem("token"), usuarioEditado);
      await fetchUsuarios();
      setSnackbar({
        open: true,
        message: "Usuario editado con éxito",
        severity: "success",
      });
    } catch (error) {
      console.error("Error al editar usuario:", error);
      setSnackbar({
        open: true,
        message: "Error al editar usuario",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddClienteUsuario = async (relacion) => {
    try {
      setIsLoading(true);
      await addRelClienteUsuario(localStorage.getItem("token"), relacion);
      await fetchUsuarios();
      setSnackbar({
        open: true,
        message: "Cliente agregado al usuario con éxito",
        severity: "success",
      });
    } catch (error) {
      console.error("Error al agregar cliente al usuario:", error);
      setSnackbar({
        open: true,
        message: "Error al agregar cliente al usuario",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
      handleCloseAddClienteModal();
    }
  };

  const handleRemoveClienteUsuario = async (relacion) => {
    try {
      setIsLoading(true);
      await deleteRelClienteUsuario(localStorage.getItem("token"), relacion);
      await fetchUsuarios();
      setSnackbar({
        open: true,
        message: "Cliente removido del usuario con éxito",
        severity: "success",
      });
    } catch (error) {
      console.error("Error al remover cliente del usuario:", error);
      setSnackbar({
        open: true,
        message: "Error al remover cliente del usuario",
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportar = async () => {
    try {
      setIsExporting(true);
      const filtros = {
        nombre: searchTerm || undefined,
        rol: filtroRol || undefined,
        tipoAuth: filtroTipoAuth || undefined,
        estadoHabilitacion: filtroEstado || "todos",
        soloAccedieron: filtroAcceso === "accedieron" ? true : undefined,
      };
      await exportarUsuariosExcel(localStorage.getItem("token"), filtros);
      setSnackbar({
        open: true,
        message: "Reporte exportado con éxito",
        severity: "success",
      });
    } catch (error) {
      console.error("Error al exportar reporte:", error);
      setSnackbar({
        open: true,
        message: "Error al exportar el reporte",
        severity: "error",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return "Nunca";
    return new Date(fecha + "Z").toLocaleString("es-CL", {
      dateStyle: "short",
      timeStyle: "short",
    });
  };

  const tableColumns = useMemo(
    () => [
      { id: "nombre", label: "Nombre", minWidth: 120, priority: 0 },
      { id: "apellido", label: "Apellido", minWidth: 120, priority: 2 },
      { id: "correo", label: "Correo", minWidth: 180, priority: 3 },
      { id: "rol", label: "Rol", minWidth: 100, priority: 1 },
      {
        id: "proveedorExterno",
        label: "Tipo Auth",
        minWidth: 100,
        priority: 3,
        format: (value, usuario) =>
          usuario.proveedorExterno
            ? `Externo (${usuario.proveedorExterno})`
            : "Local",
      },
      {
        id: "clientes",
        label: "Clientes",
        minWidth: 120,
        priority: 2,
        format: (value) =>
          value.length > 0
            ? value.map((c) => c.nombre).join("; ")
            : "Sin clientes",
      },
      {
        id: "ultimoAcceso",
        label: "Último acceso",
        minWidth: 140,
        priority: 1,
        format: (value) => formatearFecha(value),
      },
      {
        id: "cantidadIniciosSesion",
        label: "N° accesos",
        minWidth: 90,
        align: "center",
        priority: 1,
        format: (value) => value ?? 0,
      },
      {
        id: "habilitado",
        label: "Habilitado",
        minWidth: 20,
        priority: 2,
        format: (value) => (value ? "Sí" : "No"),
      },
      {
        id: "acciones",
        label: "Acciones",
        minWidth: 120,
        align: "center",
        priority: 0,
      },
    ],
    []
  );

  const visibleColumns = useMemo(() => {
    if (isMobile) {
      return tableColumns.filter((col) => col.priority <= 0);
    }
    if (isTablet) {
      return tableColumns.filter((col) => col.priority <= 1);
    }
    return tableColumns.filter((col) => col.priority <= 3);
  }, [tableColumns, isMobile, isTablet]);

  const renderNoDataMessage = () => (
    <TableContainer component={Paper}>
      <Box p={3} sx={{ width: "100%" }}>
        <Typography variant="body1" color="text.secondary">
          No se encontraron usuarios con los criterios de búsqueda actuales.
        </Typography>
      </Box>
    </TableContainer>
  );

  const renderMobileCard = (usuario) => (
    <Card key={usuario.id} sx={{ mb: 2 }}>
      <CardContent sx={{ pb: 1 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          {usuario.nombre} {usuario.apellido}
        </Typography>
        <Divider sx={{ my: 1 }} />

        <Grid container spacing={1}>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              Correo:
            </Typography>
            <Typography
              variant="body2"
              gutterBottom
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {usuario.correo}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              Rol:
            </Typography>
            <Chip
              label={usuario.rol}
              color={usuario.rol === "Administrador" ? "primary" : "default"}
              size="small"
            />
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              Tipo de Auth:
            </Typography>
            <Typography variant="body2" gutterBottom>
              {usuario.proveedorExterno
                ? `Externo (${usuario.proveedorExterno})`
                : "Local"}
            </Typography>
          </Grid>

          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              Estado:
            </Typography>
            <Chip
              label={usuario.habilitado ? "Habilitado" : "Deshabilitado"}
              color={usuario.habilitado ? "success" : "error"}
              size="small"
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              Último acceso:
            </Typography>
            <Typography variant="body2">
              {formatearFecha(usuario.ultimoAcceso)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              Inicios de sesión:
            </Typography>
            <Chip
              label={usuario.cantidadIniciosSesion ?? 0}
              size="small"
              color={
                (usuario.cantidadIniciosSesion ?? 0) > 0 ? "info" : "default"
              }
              variant={
                (usuario.cantidadIniciosSesion ?? 0) > 0 ? "filled" : "outlined"
              }
            />
          </Grid>

          {usuario.clientes && usuario.clientes.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Clientes:
              </Typography>
              <Box sx={{ mt: 0.5, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {usuario.clientes.map((cliente, index) => (
                  <Chip
                    key={index}
                    label={cliente.nombre}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.7rem", height: 24 }}
                  />
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
      <Divider />
      <CardActions
        sx={{
          p: 1.5,
          display: "flex",
          justifyContent: "center",
          flexWrap: "wrap",
          gap: 1,
        }}
      >
        <Button
          variant="contained"
          size="small"
          color="primary"
          onClick={() => handleOpenEditModal(usuario)}
          startIcon={<EditNoteIcon />}
        >
          Editar
        </Button>

        {usuario.rol !== "Administrador" && (
          <>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              onClick={() => handleOpenAddClienteModal(usuario)}
              startIcon={<PersonAddAlt1Icon />}
            >
              Agregar cliente
            </Button>

            {usuario.clientes.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={(event) => handleClick(event, usuario)}
                startIcon={<PersonRemoveAlt1Icon />}
              >
                Quitar cliente
              </Button>
            )}
          </>
        )}
      </CardActions>
    </Card>
  );

  return (
    <Grid
      container
      direction="column"
      spacing={2}
      sx={{ paddingTop: { xs: "20px", sm: "10px" } }}
    >
      {/* Cabecera */}
      <Grid item>
        <Grid
          container
          justifyContent="space-between"
          alignItems="left"
          spacing={isMobile ? 1 : 2}
          direction={isMobile ? "column" : "row"}
        >
          <Grid item xs={12} sm={4}>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              align={"left"}
              sx={{ mb: isMobile ? 1 : 0 }}
            >
              Usuarios
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            sm={8}
            sx={{
              display: "flex",
              justifyContent: isMobile ? "center" : "flex-end",
              flexWrap: "wrap",
              gap: 1.5,
            }}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={handleExportar}
              disabled={isExporting || isLoading}
              fullWidth={isMobile}
              startIcon={
                isExporting ? (
                  <CircularProgress size={18} />
                ) : (
                  <FileDownloadIcon />
                )
              }
            >
              {isExporting ? "Exportando..." : "Exportar Excel"}
            </Button>
            <Link
              to="/gestor-organizaciones"
              style={{ color: "inherit", textDecoration: "none" }}
            >
              <Button
                variant="contained"
                color="primary"
                disabled={isLoading}
                fullWidth={isMobile}
                startIcon={<DomainIcon />}
                linkComponent={Link}
              >
                Organización
              </Button>
            </Link>
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenModal}
              disabled={isLoading}
              fullWidth={isMobile}
              startIcon={<PersonAddAlt1Icon />}
            >
              Añadir usuario
            </Button>
          </Grid>
        </Grid>
      </Grid>

      {/* Tarjetas de resumen */}
      <Grid item>
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <Paper sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="h4" color="primary">
                {stats.total}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total usuarios
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="h4" color="success.main">
                {stats.accedieron}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Han accedido
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={4}>
            <Paper sx={{ p: 2, textAlign: "center" }}>
              <Typography variant="h4" color="warning.main">
                {stats.nunca}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Nunca accedieron
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Grid>

      {/* Buscador + toggle filtros */}
      <Grid item>
        <Stack direction={isMobile ? "column" : "row"} spacing={1.5}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder={
              isMobile ? "Buscar..." : "Buscar por nombre o apellido..."
            }
            value={searchTerm}
            onChange={handleSearchChange}
            size={isMobile ? "small" : "medium"}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant={showFilters ? "contained" : "outlined"}
            color="primary"
            onClick={() => setShowFilters((s) => !s)}
            startIcon={<FilterListIcon />}
            sx={{ whiteSpace: "nowrap", minWidth: isMobile ? "auto" : 140 }}
            fullWidth={isMobile}
          >
            Filtros{hayFiltrosActivos ? " •" : ""}
          </Button>
        </Stack>
      </Grid>

      {/* Panel de filtros */}
      <Grid item sx={{ pt: showFilters ? undefined : "0 !important" }}>
        <Collapse in={showFilters}>
          <Paper sx={{ p: 2 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Rol"
                  value={filtroRol}
                  onChange={(e) => {
                    setFiltroRol(e.target.value);
                    setPage(0);
                  }}
                >
                  <SelectMenuItem value="">Todos</SelectMenuItem>
                  {rolesDisponibles.map((rol) => (
                    <SelectMenuItem key={rol} value={rol}>
                      {rol}
                    </SelectMenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Tipo de autenticación"
                  value={filtroTipoAuth}
                  onChange={(e) => {
                    setFiltroTipoAuth(e.target.value);
                    setPage(0);
                  }}
                >
                  <SelectMenuItem value="">Todos</SelectMenuItem>
                  <SelectMenuItem value="Local">Local</SelectMenuItem>
                  <SelectMenuItem value="Externo">Externo</SelectMenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Estado"
                  value={filtroEstado}
                  onChange={(e) => {
                    setFiltroEstado(e.target.value);
                    setPage(0);
                  }}
                >
                  <SelectMenuItem value="">Todos</SelectMenuItem>
                  <SelectMenuItem value="habilitado">Habilitado</SelectMenuItem>
                  <SelectMenuItem value="deshabilitado">
                    Deshabilitado
                  </SelectMenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Cliente"
                  value={filtroCliente}
                  onChange={(e) => {
                    setFiltroCliente(e.target.value);
                    setPage(0);
                  }}
                >
                  <SelectMenuItem value="">Todos</SelectMenuItem>
                  {clientes.map((cliente) => (
                    <SelectMenuItem key={cliente.id} value={cliente.id.toString()}>
                      {cliente.nombre}
                    </SelectMenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2.4}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Acceso al sistema"
                  value={filtroAcceso}
                  onChange={(e) => {
                    setFiltroAcceso(e.target.value);
                    setPage(0);
                  }}
                >
                  {ESTADO_ACCESO_OPCIONES.map((op) => (
                    <SelectMenuItem key={op.value} value={op.value}>
                      {op.label}
                    </SelectMenuItem>
                  ))}
                </TextField>
              </Grid>             
              {hayFiltrosActivos && (
                <Grid item xs={12}>
                  <Button
                    size="small"
                    color="inherit"
                    onClick={limpiarFiltros}
                    startIcon={<ClearIcon />}
                  >
                    Limpiar filtros
                  </Button>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Collapse>
      </Grid>

      {/* Contenido principal */}
      <Grid item>
        {isLoading && !initialDataLoaded ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : displayedUsuarios.length > 0 ? (
          <>
            {isMobile ? (
              <Box sx={{ mt: 2 }}>{displayedUsuarios.map(renderMobileCard)}</Box>
            ) : (
              <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
                <Table size={isTablet ? "small" : "medium"} stickyHeader>
                  <TableHead>
                    <TableRow>
                      {visibleColumns.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.align || "left"}
                          sx={{
                            minWidth: column.minWidth,
                            padding: isTablet ? "8px 4px" : "10px 8px",
                            whiteSpace: "nowrap",
                            fontWeight: "bold",
                          }}
                        >
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedUsuarios.map((usuario) => (
                      <TableRow
                        key={usuario.id}
                        hover
                        sx={{
                          "&:nth-of-type(odd)": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        {visibleColumns.map((column) => {
                          if (column.id === "acciones") {
                            return (
                              <TableCell
                                key={column.id}
                                align="center"
                                sx={{ padding: "4px" }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Tooltip title="Editar usuario">
                                    <IconButton
                                      size="small"
                                      color="primary"
                                      onClick={() => handleOpenEditModal(usuario)}
                                    >
                                      <EditNoteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>

                                  {usuario.rol !== "Administrador" && (
                                    <>
                                      <Tooltip title="Agregar cliente">
                                        <IconButton
                                          size="small"
                                          color="primary"
                                          onClick={() =>
                                            handleOpenAddClienteModal(usuario)
                                          }
                                        >
                                          <PersonAddAlt1Icon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>

                                      {usuario.clientes.length > 0 && (
                                        <Tooltip title="Quitar cliente">
                                          <IconButton
                                            size="small"
                                            color="error"
                                            onClick={(event) =>
                                              handleClick(event, usuario)
                                            }
                                          >
                                            <PersonRemoveAlt1Icon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </>
                                  )}
                                </Box>
                              </TableCell>
                            );
                          }

                          const value = usuario[column.id];

                          return (
                            <TableCell
                              key={column.id}
                              align={column.align || "left"}
                              sx={{
                                padding: isTablet ? "8px 4px" : "10px 8px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                maxWidth: isTablet ? "100px" : "200px",
                              }}
                            >
                              {column.id === "habilitado" ? (
                                <Chip
                                  size="small"
                                  label={usuario.habilitado ? "Sí" : "No"}
                                  color={
                                    usuario.habilitado ? "success" : "error"
                                  }
                                />
                              ) : column.id === "rol" ? (
                                <Chip
                                  size="small"
                                  label={usuario.rol}
                                  color={
                                    usuario.rol === "Administrador"
                                      ? "primary"
                                      : "default"
                                  }
                                />
                              ) : column.id === "cantidadIniciosSesion" ? (
                                <Chip
                                  size="small"
                                  label={value ?? 0}
                                  color={(value ?? 0) > 0 ? "info" : "default"}
                                  variant={
                                    (value ?? 0) > 0 ? "filled" : "outlined"
                                  }
                                />
                              ) : column.format ? (
                                column.format(value, usuario)
                              ) : (
                                value
                              )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <TablePagination
              rowsPerPageOptions={isMobile ? [5, 10] : [5, 10, 25]}
              component="div"
              count={filteredUsuarios.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={isMobile ? "Filas:" : "Filas por página:"}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} de ${count}`
              }
            />
          </>
        ) : (
          renderNoDataMessage()
        )}
      </Grid>

      {/* Modales */}
      <NuevoUsuarioModal
        open={openModal}
        onClose={handleCloseModal}
        onSubmit={handleNuevoUsuario}
        isLoading={isLoading}
      />
      <EditarUsuarioModal
        open={openEditModal}
        onClose={handleCloseEditModal}
        onSubmit={handleEditarusuario}
        usuario={usuarioSeleccionado}
      />
      <AgregarClienteUsuarioModal
        open={openAddClienteModal}
        onClose={handleCloseAddClienteModal}
        onSubmit={handleAddClienteUsuario}
        usuario={usuarioSeleccionado}
      />

      {/* Menu para quitar clientes */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {selectedUsuario &&
          selectedUsuario.clientes.map((cliente) => (
            <MenuItem
              key={cliente.id}
              onClick={() => handleRemoveCliente(cliente.id)}
            >
              {cliente.nombre}
            </MenuItem>
          ))}
      </Menu>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default GestorUsuarios;